import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { DataGrid } from '@mui/x-data-grid';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';
import useAuth from '../hooks/useAuth';
import {
  getBillingPlan,
  getBillingPeriod,
  listInvoices,
  getBillingUsageBreakdown,
  getPaymentPortalUrl,
  downloadInvoicePdf,
} from '../services/collegeApi';
import { formatCurrencyCents, formatDate, formatPercent } from '../utils/formatters';
import { INVOICE_STATUSES } from '../utils/constants';

export default function BillingPage() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [period, setPeriod] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [breakdown, setBreakdown] = useState({ breakdown: [], rate_cents: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    if (!user?.college_id) return;
    setLoading(true);
    Promise.all([
      getBillingPlan(user.college_id).catch(() => null),
      getBillingPeriod(user.college_id).catch(() => null),
      listInvoices(user.college_id).catch(() => ({ invoices: [] })),
      getBillingUsageBreakdown(user.college_id).catch(() => ({ breakdown: [], rate_cents: 0 })),
    ])
      .then(([p, per, inv, b]) => {
        setPlan(p);
        setPeriod(per);
        setInvoices(inv?.invoices || []);
        setBreakdown(b || { breakdown: [], rate_cents: 0 });
      })
      .catch((err) => setError(err?.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, [user]);

  const handleManagePayment = async () => {
    setOpeningPortal(true);
    try {
      const result = await getPaymentPortalUrl(user.college_id);
      if (result?.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setOpeningPortal(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const result = await downloadInvoicePdf(user.college_id, invoiceId);
      if (result?.url) {
        window.open(result.url, '_blank', 'noopener');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Billing</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage your plan, payment method, and invoice history. Per-student rates apply each semester.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <CurrentPlanCard
                plan={plan}
                onManagePayment={handleManagePayment}
                openingPortal={openingPortal}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CurrentPeriodCard period={period} plan={plan} />
            </Grid>
          </Grid>

          <UsageBreakdownSection breakdown={breakdown.breakdown} rateCents={breakdown.rate_cents} />

          <InvoiceHistorySection invoices={invoices} onDownload={handleDownloadInvoice} />
        </Stack>
      )}
    </Box>
  );
}

// ─── Section: Current Plan ───────────────────────────────────────────────────
function CurrentPlanCard({ plan, onManagePayment, openingPortal }) {
  if (!plan) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Alert severity="info">No plan on file.</Alert>
      </Paper>
    );
  }

  const isInvoice = plan.payment_method?.type === 'invoice';

  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          backgroundColor: 'primary.main', color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ReceiptLongIcon />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Current plan
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {plan.plan_name}
          </Typography>
        </Box>
      </Box>

      <Stack spacing={1.5} sx={{ flex: 1 }}>
        <KV label="Per-student rate" value={
          <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
            {formatCurrencyCents(plan.per_student_rate_cents, plan.currency)}
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              / student / semester
            </Typography>
          </Typography>
        } />
        <KV label="Billing email" value={plan.billing_email} mono />
        <Divider sx={{ my: 0.5 }} />
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Payment method
          </Typography>
          <Box sx={{
            mt: 0.75,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}>
            {isInvoice ? (
              <>
                <Chip label="PO / Invoice" color="secondary" size="small" sx={{ fontWeight: 600 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {plan.payment_method?.po_number || '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Terms: {plan.payment_method?.terms || 'Net 30'}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{
                  width: 38, height: 26, borderRadius: 0.5, backgroundColor: '#1a1f71',
                  color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                }}>
                  {plan.payment_method?.brand || 'Card'}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                    •••• •••• •••• {plan.payment_method?.last4 || '????'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Expires {String(plan.payment_method?.exp_month || '').padStart(2, '0')}/
                    {plan.payment_method?.exp_year || ''}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={openingPortal ? <CircularProgress size={16} /> : <CreditCardIcon />}
          endIcon={!isInvoice && <OpenInNewIcon fontSize="small" />}
          onClick={onManagePayment}
          disabled={openingPortal}
          fullWidth
        >
          {isInvoice ? 'Update Billing Contact' : 'Manage Payment Method'}
        </Button>
      </Box>
    </Paper>
  );
}

// ─── Section: Current Period Summary ─────────────────────────────────────────
function CurrentPeriodCard({ period, plan }) {
  if (!period) {
    return (
      <Paper sx={{ p: 3, height: '100%' }}>
        <Alert severity="info">No active billing period.</Alert>
      </Paper>
    );
  }
  const cfg = INVOICE_STATUSES[period.status] || { label: period.status, color: '#6b7280' };
  const isPO = plan?.payment_method?.type === 'invoice';

  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Current period
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {period.period_label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(period.period_start)} → {formatDate(period.period_end)}
          </Typography>
        </Box>
        <Chip
          label={cfg.label}
          sx={{
            backgroundColor: `${cfg.color}18`,
            color: cfg.color,
            fontWeight: 700,
            border: `1px solid ${cfg.color}40`,
          }}
        />
      </Box>

      <Box sx={{ flex: 1 }}>
        <Box sx={{
          p: 2,
          borderRadius: 1.5,
          backgroundColor: '#f8fafc',
          border: '1px solid',
          borderColor: 'divider',
          mb: 2,
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Amount due
          </Typography>
          <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '2rem', lineHeight: 1.1 }}>
            {formatCurrencyCents(period.amount_due_cents, plan?.currency)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatCurrencyCents(period.rate_cents, plan?.currency)} × {period.billable_students} active student
            {period.billable_students === 1 ? '' : 's'}
          </Typography>
        </Box>

        <Stack spacing={1}>
          {period.invoice_number && (
            <KV label="Invoice number" value={period.invoice_number} mono />
          )}
          {isPO && period.po_number && (
            <KV label="PO number" value={period.po_number} mono />
          )}
          <KV label="Next invoice date" value={formatDate(period.next_invoice_date)} />
        </Stack>
      </Box>
    </Paper>
  );
}

// ─── Section: Usage Breakdown ────────────────────────────────────────────────
function UsageBreakdownSection({ breakdown, rateCents }) {
  const totalCost = breakdown.reduce((acc, c) => acc + (c.cost_cents || 0), 0);
  const maxCost = breakdown.reduce((m, c) => Math.max(m, c.cost_cents || 0), 0);

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Usage breakdown</Typography>
          <Typography variant="body2" color="text.secondary">
            Per-cohort billable headcount and cost contribution.
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Total
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1 }}>
            {formatCurrencyCents(totalCost)}
          </Typography>
        </Box>
      </Box>

      {breakdown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No cohorts configured.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {breakdown.map((c) => {
            const pct = formatPercent(c.cost_cents, maxCost);
            return (
              <Box
                key={c.cohort_id}
                sx={{
                  p: 1.75,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  opacity: c.status === 'COMPLETED' ? 0.7 : 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {c.cohort_name}
                    </Typography>
                    <Chip
                      label={c.status === 'ACTIVE' ? 'Active' : c.status === 'COMPLETED' ? 'Completed' : c.status}
                      size="small"
                      sx={{
                        height: 20,
                        backgroundColor: c.status === 'ACTIVE' ? '#16a34a18' : '#0d948818',
                        color: c.status === 'ACTIVE' ? '#16a34a' : '#0d9488',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrencyCents(c.cost_cents)}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 0.75 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <GroupIcon fontSize="inherit" />
                    <Typography variant="caption">
                      {c.billable_students} billable / {c.enrolled_students} enrolled
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <AssignmentIcon fontSize="inherit" />
                    <Typography variant="caption">
                      {c.assessments_completed} assessment{c.assessments_completed === 1 ? '' : 's'} completed
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatCurrencyCents(rateCents)} / student
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e5e7eb',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: c.status === 'ACTIVE' ? '#1e3a8a' : '#0d9488',
                    },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}

// ─── Section: Invoice History ────────────────────────────────────────────────
function InvoiceHistorySection({ invoices, onDownload }) {
  const columns = [
    {
      field: 'issued_at',
      headerName: 'Date',
      width: 130,
      renderCell: (p) => formatDate(p.row.issued_at),
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice #',
      width: 160,
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {p.row.invoice_number}
        </Typography>
      ),
    },
    { field: 'period_label', headerName: 'Period', flex: 1, minWidth: 200 },
    {
      field: 'students_billed',
      headerName: 'Students',
      width: 110,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {p.row.students_billed}
        </Typography>
      ),
    },
    {
      field: 'amount_cents',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {formatCurrencyCents(p.row.amount_cents)}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (p) => {
        const cfg = INVOICE_STATUSES[p.row.status] || { label: p.row.status, color: '#6b7280' };
        return (
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              backgroundColor: `${cfg.color}18`,
              color: cfg.color,
              fontWeight: 600,
              border: `1px solid ${cfg.color}40`,
            }}
          />
        );
      },
    },
    {
      field: 'pdf',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Tooltip title={p.row.pdf_url ? 'Download PDF' : 'PDF not yet available'}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDownload(p.row.invoice_id); }}
              disabled={!p.row.pdf_url}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>Invoice history</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Past and pending invoices. PDFs become available after invoices are issued.
      </Typography>
      <Box sx={{ height: 400 }}>
        <DataGrid
          rows={invoices.map((inv) => ({ id: inv.invoice_id, ...inv }))}
          columns={columns}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: 'issued_at', sort: 'desc' }] },
          }}
          density="comfortable"
        />
      </Box>
    </Paper>
  );
}

// ─── Helper: KV row ──────────────────────────────────────────────────────────
function KV({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: mono ? 'monospace' : undefined,
          fontWeight: 600,
          textAlign: 'right',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={typeof value === 'string' ? value : undefined}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}
